import { useRef } from "react";
import { Button } from "../primitives/Button.tsx";
import { MUTED_TEXT, rowStyle, stackStyle } from "../styles.ts";
import { attachmentCountLabel, attachmentDetailLabel, type AttachmentView } from "../logic/attachments.ts";

export type { AttachmentView } from "../logic/attachments.ts";

export interface DraftAttachmentListProps {
  readonly attachments: readonly AttachmentView[];
  readonly rejected?: readonly string[];
  readonly disabled?: boolean;
  readonly onRemove: (draftId: string) => void;
  readonly onRetry?: (draftId: string) => void;
  readonly onDismissRejected?: () => void;
}

const CHIP = {
  alignItems: "center",
  background: "var(--dayvise-surface-raised, transparent)",
  border: "1px solid var(--dayvise-border-subtle, currentColor)",
  borderRadius: "var(--dayvise-radius-md)",
  display: "flex",
  gap: "var(--dayvise-space-sm)",
  padding: "var(--dayvise-space-xs) var(--dayvise-space-sm)",
} as const;

export function DraftAttachmentList({ attachments, rejected = [], disabled = false, onRemove, onRetry, onDismissRejected }: DraftAttachmentListProps) {
  if (attachments.length === 0 && rejected.length === 0) return null;
  return (
    <section aria-label="Attachments for the next message" style={stackStyle("var(--dayvise-space-xs)")}>
      {attachments.length > 0 ? <p style={{ ...MUTED_TEXT, margin: 0 }}>{`Selected input: ${attachmentCountLabel(attachments)}. These are inputs, not generated output.`}</p> : null}
      <ul style={{ ...stackStyle("var(--dayvise-space-xs)"), listStyle: "none", margin: 0, padding: 0 }}>
        {attachments.map((attachment) => (
          <li key={attachment.draftId} style={CHIP}>
            {attachment.previewUrl !== null ? <img src={attachment.previewUrl} alt={`Preview of ${attachment.name}`} style={{ borderRadius: "var(--dayvise-radius-sm)", height: "2.5rem", objectFit: "cover", width: "2.5rem" }} /> : null}
            <span style={{ display: "grid", gap: "0.125rem", minWidth: 0 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attachment.name}</span>
              <span style={MUTED_TEXT}>{attachmentDetailLabel(attachment)}</span>
            </span>
            {attachment.status === "uploading" ? <progress aria-label={`Uploading ${attachment.name}`} /> : null}
            {attachment.error !== null ? <span role="alert">{attachment.error}</span> : null}
            {attachment.canRetry && onRetry ? <Button size="sm" variant="ghost" disabled={disabled} onClick={() => onRetry(attachment.draftId)} aria-label={`Retry ${attachment.name}`}>Retry</Button> : null}
            <Button size="sm" variant="ghost" disabled={disabled} onClick={() => onRemove(attachment.draftId)} aria-label={`Remove ${attachment.name} from this message`}>Remove</Button>
          </li>
        ))}
      </ul>
      {rejected.length > 0 ? (
        <div style={{ ...stackStyle("var(--dayvise-space-xs)") }} role="alert">
          <ul style={{ margin: 0 }}>
            {rejected.map((problem) => <li key={problem}>{problem}</li>)}
          </ul>
          {onDismissRejected ? <Button size="sm" variant="ghost" onClick={onDismissRejected}>Dismiss</Button> : null}
        </div>
      ) : null}
    </section>
  );
}

export interface DraftAttachmentPickerProps {
  readonly disabled?: boolean;
  readonly accept?: string;
  readonly label?: string;
  readonly onFiles: (files: readonly File[]) => void;
}

export function DraftAttachmentPicker({ disabled = false, accept = "image/png,image/jpeg,image/webp,image/gif,.md,.markdown,.txt,.csv,.json,.yaml,.yml,.xml,.ndjson,.ts,.tsx,.js,.jsx,.py,.rs,.go,.java,.kt,.sql,.sh,.toml,.ini", label = "Attach files", onFiles }: DraftAttachmentPickerProps) {
  const picker = useRef<HTMLInputElement>(null);
  return (
    <span style={rowStyle("var(--dayvise-space-xs)")}>
      <input
        ref={picker}
        type="file"
        multiple
        accept={accept}
        aria-label={label}
        hidden
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = "";
          if (files.length > 0) onFiles(files);
        }}
      />
      <Button size="sm" variant="ghost" disabled={disabled} onClick={() => picker.current?.click()}>{label}</Button>
    </span>
  );
}
